import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MdOutlineMap, MdOutlineSatelliteAlt, MdOutlineLayers, MdOutlinePlace, MdMyLocation, MdOutlineZoomOutMap, MdOutlineExpandMore } from 'react-icons/md';

import { MAX_SLOPE_OPTIONS, TERRAIN_COLOR_MODES, DEFAULT_MAX_SLOPE_PERCENT } from './slopeBands';
export const LAYER_GROUPS = [
  { title: 'Terrain', items: [
    { key: 'contours', label: 'Contour lines', hint: 'Elevation contours with labels in feet' },
    { key: 'terrain3d', label: '3D view', hint: 'Tilt the map: true-scale USGS 1 m terrain with buildings (zoom 15 and closer)' },
  ] },
  { title: 'Site', items: [
    { key: 'parcel', label: 'Parcel outline', needs: 'parcel', hint: 'Assessor parcel boundary' },
    { key: 'zone', label: 'Zoning district', needs: 'zone', hint: 'Boundary of the zoning district' },
    { key: 'terrain', label: 'Parcel elevation', needs: 'terrain', hint: 'USGS 1 m lidar elevation across the parcel, with the low and high points' },
    { key: 'parcelContours', label: 'Parcel contours', needs: 'terrain', hint: 'Smoothed contour lines through the 1 m elevation grid; the highlight sweeps from the high point down to the low point' },
    { key: 'treeCanopy', label: 'Tree canopy', needs: 'treeCanopy', hint: '1 m canopy-height model, trees ≥ 3 m; parcel solid, surroundings faded' },
    { key: 'footprints', label: 'Building footprints', needs: 'footprints', hint: 'Overture building outlines from the API: on-parcel solid, neighbours crossing the line hatched, surroundings faint (separate from the basemap 3D buildings)' },
    { key: 'flood', label: 'Flood zones', needs: 'flood', hint: 'FEMA flood zones (NFHL): floodway dark blue, 1% annual chance (high risk) blue, 0.2% (moderate) light blue, base flood elevation lines dashed; on the lot solid, around it faded' },
  ] },
  { title: 'Surroundings', items: [
    { key: 'anchors', label: 'Commercial anchors', needs: 'places', hint: 'Named brands: nearest location per brand, sized by editorial anchor score' },
    { key: 'publicPlaces', label: 'Public & recreational places', needs: 'places', hint: 'Named parks, playgrounds, pools, trails, libraries and other public places, nearest first — mapped, not scored' },
    { key: 'externalities', label: 'Adverse uses', needs: 'places', hint: 'Tagged adverse uses, sized by signed distance-decayed screening score' },
    { key: 'comps', label: 'Sale comps', needs: 'comps', hint: 'Nearby single-family listings: violet = new construction, blue = existing; filled = active, hollow = inactive' },
    { key: 'jobRadii', label: 'Job radii', needs: 'result', hint: '1 / 2 / 5 / 10 mile employment rings' },
  ] },
];

/**
 * Map settings live above the canvas so the imagery stays clean: base style,
 * a Layers menu, and the point actions (drop pin, recenter, fit radius).
 */
export const EXAGGERATION_OPTIONS = [
  { value: 1, label: '×1 true' },
  { value: 2, label: '×2' },
  { value: 3, label: '×3' },
];

// Tree-canopy search radius (feet) — a request option, not a map toggle: the
// canopy geometry and the surround summary come back for this ring, so changing
// it marks the evaluation stale the same way the mile radii do. 330 ft ≈ 100 m.
export const CANOPY_RADIUS_OPTIONS = [165, 330, 660, 1000];
export const DEFAULT_CANOPY_RADIUS_FT = 330;

// Building-footprints search radius (feet), same idea: a request option that
// sets `summary.surround` and, when neighbouring outlines are asked for, how
// far the drawn geometry reaches. The API accepts 50–1000 ft. The steps are
// deliberately distinct from the canopy ones so the two controls never read
// the same.
export const FOOTPRINTS_RADIUS_OPTIONS = [100, 200, 400, 800];
export const DEFAULT_FOOTPRINTS_RADIUS_FT = 200;

// Flood-zone surroundings radius (feet), a request option like the two above:
// the zones on the lot always come back; a radius adds the zones around it (how
// close the 1% zone or floodway is) and the ring shares. 0 = the lot only.
// Steps distinct from the canopy and footprints ones, like those two.
export const FLOOD_RADIUS_OPTIONS = [0, 250, 500, 750];
export const DEFAULT_FLOOD_RADIUS_FT = 500;

// Keep the Layers menu inside the window: it opens under its button and
// scrolls once the list is taller than the room left below.
const POPOVER_MIN_HEIGHT = 220;
const POPOVER_BOTTOM_GAP = 16;

export default function MapSettingsBar({
  mapStyle,
  onMapStyleChange,
  pinMode,
  onPinModeChange,
  hasResult,
  onRecenter,
  onFitRadius,
  layers,
  available,
  onLayerChange,
  canopyRadiusFt = DEFAULT_CANOPY_RADIUS_FT,
  onCanopyRadiusChange,
  canopyRadiusDirty = false,
  footprintsRadiusFt = DEFAULT_FOOTPRINTS_RADIUS_FT,
  onFootprintsRadiusChange,
  footprintsRadiusDirty = false,
  showNeighbouringFootprints = false,
  onShowNeighbouringFootprintsChange,
  neighbouringFootprintsDirty = false,
  floodRadiusFt = DEFAULT_FLOOD_RADIUS_FT,
  onFloodRadiusChange,
  floodRadiusDirty = false,
}) {
  const [open, setOpen] = useState(false);
  const [popoverMaxHeight, setPopoverMaxHeight] = useState(null);
  const menuRef = useRef(null);
  const popoverRef = useRef(null);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const fit = () => {
      const el = popoverRef.current;
      if (!el) return;
      const room = window.innerHeight - el.getBoundingClientRect().top - POPOVER_BOTTOM_GAP;
      setPopoverMaxHeight(Math.max(POPOVER_MIN_HEIGHT, Math.floor(room)));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const activeCount = LAYER_GROUPS.flatMap((g) => g.items).filter((it) => layers[it.key] && (!it.needs || available[it.needs])).length;

  return (
    <div className="le-map-settings">
      <div className="le-segmented" role="tablist" aria-label="Map style">
        <button type="button" role="tab" aria-selected={mapStyle === 'satellite'} className={mapStyle === 'satellite' ? 'active' : ''} onClick={() => onMapStyleChange('satellite')}>
          <MdOutlineSatelliteAlt size={15} /> Satellite
        </button>
        <button type="button" role="tab" aria-selected={mapStyle === 'streets'} className={mapStyle === 'streets' ? 'active' : ''} onClick={() => onMapStyleChange('streets')}>
          <MdOutlineMap size={15} /> Streets
        </button>
      </div>

      <div className="le-layers" ref={menuRef}>
        <button type="button" className={`le-action-btn le-layers-btn ${open ? 'open' : ''}`} onClick={() => setOpen((v) => !v)} aria-haspopup="true" aria-expanded={open}>
          <MdOutlineLayers size={16} /> Layers
          <span className="le-layers-count">{activeCount}</span>
          <MdOutlineExpandMore size={16} />
        </button>
        {open ? (
          <div className="le-layers-popover" role="menu" aria-label="Map layers" ref={popoverRef} style={popoverMaxHeight ? { maxHeight: popoverMaxHeight } : undefined}>
            {LAYER_GROUPS.map((group) => (
              <div key={group.title} className="le-layers-group">
                <div className="le-layers-group-title">{group.title}</div>
                {group.title === 'Terrain' && layers.terrain3d ? (
                  <div className="le-layers-sub" title="Vertical exaggeration of the 3D terrain; ×1 is true scale. Buildings are always true scale.">
                    <span className="le-layers-sub-label">Vertical scale</span>
                    <div className="le-segmented le-segmented-sm" role="radiogroup" aria-label="Vertical exaggeration">
                      {EXAGGERATION_OPTIONS.map((opt) => (
                        <button key={opt.value} type="button" role="radio" aria-checked={Number(layers.terrainExaggeration || 1) === opt.value} className={Number(layers.terrainExaggeration || 1) === opt.value ? 'active' : ''} onClick={() => onLayerChange('terrainExaggeration', opt.value)}>{opt.label}</button>
                      ))}
                    </div>
                  </div>
                ) : null}
                {group.items.map((item) => {
                  const enabled = !item.needs || Boolean(available[item.needs]);
                  const terrainMode = layers.terrainMode === 'slope' ? 'slope' : 'elevation';
                  const maxSlope = Number(layers.maxSlopePercent) || DEFAULT_MAX_SLOPE_PERCENT;
                  return (
                    <React.Fragment key={item.key}>
                      <label className={`le-layers-item ${enabled ? '' : 'disabled'}`} title={enabled ? item.hint : `${item.hint} — available after an evaluation`}>
                        <input type="checkbox" checked={Boolean(layers[item.key]) && enabled} disabled={!enabled} onChange={(e) => onLayerChange(item.key, e.target.checked)} />
                        <span>{item.label}</span>
                      </label>
                      {item.key === 'terrain' && enabled && layers.terrain ? (
                        <div className="le-layers-sub" title="Color the parcel grid by elevation, or by slope band (flat → hillside)">
                          <span className="le-layers-sub-label">Color by</span>
                          <div className="le-segmented le-segmented-sm" role="radiogroup" aria-label="Terrain color">
                            {TERRAIN_COLOR_MODES.map((opt) => (
                              <button key={opt.value} type="button" role="radio" aria-checked={terrainMode === opt.value} className={terrainMode === opt.value ? 'active' : ''} onClick={() => onLayerChange('terrainMode', opt.value)}>{opt.label}</button>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      {item.key === 'treeCanopy' && onCanopyRadiusChange ? (
                        <div className={`le-layers-sub ${canopyRadiusDirty ? 'le-layers-sub-dirty' : ''}`} title="How far past the parcel line the canopy geometry and the surround summary reach; applies on the next evaluation (re-run after changing it)">
                          <span className="le-layers-sub-label">Surround radius</span>
                          <div className="le-segmented le-segmented-sm" role="radiogroup" aria-label="Canopy surround radius">
                            {CANOPY_RADIUS_OPTIONS.map((v) => (
                              <button key={v} type="button" role="radio" aria-checked={Number(canopyRadiusFt) === v} className={Number(canopyRadiusFt) === v ? 'active' : ''} onClick={() => onCanopyRadiusChange(v)}>{v} ft</button>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      {item.key === 'footprints' && onFootprintsRadiusChange ? (
                        <>
                          <div className={`le-layers-sub ${footprintsRadiusDirty ? 'le-layers-sub-dirty' : ''}`} title="How far past the parcel line the footprint surround summary (and the neighbouring outlines, when shown) reach; applies on the next evaluation (re-run after changing it)">
                            <span className="le-layers-sub-label">Footprints radius</span>
                            <div className="le-segmented le-segmented-sm" role="radiogroup" aria-label="Footprints surround radius">
                              {FOOTPRINTS_RADIUS_OPTIONS.map((v) => (
                                <button key={v} type="button" role="radio" aria-checked={Number(footprintsRadiusFt) === v} className={Number(footprintsRadiusFt) === v ? 'active' : ''} onClick={() => onFootprintsRadiusChange(v)}>{v} ft</button>
                              ))}
                            </div>
                          </div>
                          {onShowNeighbouringFootprintsChange ? (
                            <label className={`le-layers-item le-layers-item-nested ${neighbouringFootprintsDirty ? 'le-layers-sub-dirty' : ''}`} title="Also fetch and draw the outlines of buildings within the footprints radius (a request option: turning it on needs a re-run; turning it off hides them at once)">
                              <input type="checkbox" checked={Boolean(showNeighbouringFootprints)} onChange={(e) => onShowNeighbouringFootprintsChange(e.target.checked)} />
                              <span>Show neighbouring footprints</span>
                            </label>
                          ) : null}
                        </>
                      ) : null}
                      {item.key === 'flood' && onFloodRadiusChange ? (
                        <div className={`le-layers-sub ${floodRadiusDirty ? 'le-layers-sub-dirty' : ''}`} title="How far past the lot line to fetch and draw flood zones (the zones on the lot are always shown); applies on the next evaluation (re-run after changing it)">
                          <span className="le-layers-sub-label">Around the lot</span>
                          <div className="le-segmented le-segmented-sm" role="radiogroup" aria-label="Flood zone surroundings radius">
                            {FLOOD_RADIUS_OPTIONS.map((v) => (
                              <button key={v} type="button" role="radio" aria-checked={Number(floodRadiusFt) === v} className={Number(floodRadiusFt) === v ? 'active' : ''} onClick={() => onFloodRadiusChange(v)}>{v ? `${v} ft` : 'Lot only'}</button>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      {item.key === 'terrain' && enabled && layers.terrain && terrainMode === 'slope' ? (
                        <div className="le-layers-sub" title="Steepest slope you would still build on; cells steeper than this are hatched red">
                          <span className="le-layers-sub-label">Max slope</span>
                          <div className="le-segmented le-segmented-sm" role="radiogroup" aria-label="Maximum buildable slope">
                            {MAX_SLOPE_OPTIONS.map((v) => (
                              <button key={v} type="button" role="radio" aria-checked={maxSlope === v} className={maxSlope === v ? 'active' : ''} onClick={() => onLayerChange('maxSlopePercent', v)}>{v}%</button>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="le-map-settings-spacer" />

      <button type="button" className={`le-action-btn ${pinMode ? 'le-action-btn-active' : ''}`} onClick={() => onPinModeChange(!pinMode)} title="Drop a pin anywhere on the map to evaluate that coordinate" aria-pressed={pinMode}>
        <MdOutlinePlace size={16} /> {pinMode ? 'Click the map…' : 'Drop pin'}
      </button>
      {hasResult ? (
        <>
          <button type="button" className="le-action-btn le-action-btn-icon" onClick={onRecenter} title="Recenter on the subject" aria-label="Recenter on the subject"><MdMyLocation size={16} /></button>
          <button type="button" className="le-action-btn le-action-btn-icon" onClick={onFitRadius} title="Zoom out to the demographics radius" aria-label="Zoom out to the demographics radius"><MdOutlineZoomOutMap size={16} /></button>
        </>
      ) : null}
    </div>
  );
}
